/* eslint-disable node/no-callback-literal */
import { AUTH, SOCIAL_CHECK_USER } from 'constants/api'
import { useContext, useState } from "react"
import { AuthError, FacebookAuthProvider, getAuth, signInWithCredential, UserCredential } from '@firebase/auth'
import axios, { AxiosError } from 'axios'
import useRedirect from 'hooks/use-redirect'
import utmServices from 'services/utm-services'
import FacebookIcon from 'components/icon/facebook'
import MetaCAPIService from 'services/meta-capi.services'
import { dlSetUserInfo, dlTrackEvent, createUserProperties } from 'components/dataLayer/dataLayer'
import { getBirthdayLocal } from 'utilities/birthday'
import { UserData } from 'contexts/session-context/session-reducer'
import useRouter from 'hooks/use-router'
import CONFIGS from 'build/Config'
import textServices from 'services/text-services'
import { LoaderContext } from '../../../contexts/loader-context'
import { goToUrlFacebook } from './goToUrlFacebook'

function Facebook({ setSession, setUser, setLoginError, setError, setFirebaseError, setAllowCookies, setPrize }) {

  const { setLoad } = useContext(LoaderContext)
  const [{ url }, { redirect }] = useRedirect();
  const auth = getAuth()
  const { push } = useRouter();
  const [birthday] = useState(getBirthdayLocal())

  const completeData = (user: UserData, session: Object) => {
    setSession(session)
    setUser(user)

    const external = localStorage.getItem('toexternal')
    if (external) {
      localStorage.removeItem('toexternal')
      return window.location.assign(decodeURI(JSON.parse(external)))
    };

    if (url) return redirect();

    const availableFields = CONFIGS?.notAvailableFields
    const isComplete = ((availableFields?.includes('nickName') ? false : !user?.nickName) || !user?.birthday || (!user?.genre || !user?.genreId))
    if (!CONFIGS?.signUp?.base && isComplete) return push('/information/update?isNew=true');

    return push('/home')
  }

  const callback = async (data?: {
    bodyLogin: any
    session: UserCredential
    loginType: string | null
  }) => {
    setLoad(true)
    const { session, bodyLogin, loginType } = data || {}
    try {
      const accessToken = (session.user as any).accessToken
      const consumption = localStorage.getItem('consumption')

      if (consumption) {
        bodyLogin.consumptionCenterId = JSON.parse(consumption) ?? 0
      }

      const { data: { prize, profile: user } } = await axios.post(AUTH, bodyLogin, {
        headers: { Authorization: `Bearer ${accessToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
      })

      if (bodyLogin?.termsOfUse) {
        gtag('event', 'sign_up', { event_label: `SignUp - ${loginType}`, method: `${loginType}` })
        dlTrackEvent('sign_up', { method: `${loginType}` }, user?.hashId ?? user?.id)
        MetaCAPIService.emitCompleteRegistration(session.user.email, bodyLogin?.firstName, bodyLogin?.lastName, '', window.location.href, 'Meta', MetaCAPIService.uuidv4())
      }

      dlSetUserInfo(user?.hashId ?? user?.id)
      createUserProperties(user, loginType)
      if (prize) {
        return setPrize({
          prize,
          name: user?.firstName,
          callback: () => completeData(user, session)
        })
      }

      return completeData(user, session)
    } catch (error) {
      setAllowCookies({ callback, isAllow: false })
      const code = (error as AxiosError).response?.data.code || 99999
      setLoginError(code)
    } finally {
      setLoad(false)
    }
  }

  const loginSDK = async (response: any) => {
    try {
      setLoad(true)
      const accessToken = response?.authResponse?.accessToken;
      const credentials = FacebookAuthProvider.credential(accessToken)
      const result = await signInWithCredential(auth, credentials);
      const names = result?.user?.displayName?.split(' ') || ['', '']
      const firstName = names[0]
      const lastName = names[names.length - 1]

      const { status } = await axios.get(`${SOCIAL_CHECK_USER}`, {
        headers: { Authorization: `Bearer ${(result.user as any).accessToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
      })

      const utm = utmServices.utmRead()
      const lang = textServices.language

      const bodyLogin: any = { accessToken, firstName, lastName, utm, lang }

      const bithdayUser = (result as any)?.user?.reloadUserInfo?.dateOfBirth ? new Date((result as any)?.user?.reloadUserInfo?.dateOfBirth) : birthday

      if (bithdayUser && !Number.isNaN(bithdayUser)) {
        bodyLogin.birthday = bithdayUser;
      }

      const callbackBody = { session: result, loginType: 'facebook' }

      if (status === 204) {
        return setAllowCookies({
          callback: ({ termsOfUse, subscribe, code, privacyPolicy }: { termsOfUse: boolean; subscribe: boolean; code?: string, privacyPolicy?: boolean }) =>
            callback({
              ...callbackBody,
              bodyLogin: { ...bodyLogin, termsOfUse, subscribe, code, privacyPolicy }
            }),
          isAllow: true
        })
      }

      gtag('event', 'login', { event_label: `Login - facebook`, method: `facebook` })
      dlTrackEvent('login', { method: `facebook` })
      await callback({ ...callbackBody, bodyLogin })
    } catch (error) {
      if ((error as AxiosError).isAxiosError) {
        const code = (error as AxiosError)?.response?.data.code || 99999
        setLoginError(code)
      } else {
        const typedError = error as AuthError
        setError(typedError)
        const isAccountExistsError = typedError.code === 'auth/account-exists-with-different-credential';
        const errorKey = isAccountExistsError ? `${typedError.code}-facebook` : typedError.code;
        setFirebaseError(errorKey);
      }
    } finally {
      setLoad(false)
    }
  }

  const handleClick = () => {
    dlTrackEvent('user_interaction', {
      action: "click",
      element: 'facebook',
      section: "login"
    })
    gtag('event', 'Click', { event_category: GTM_EVENT_CATEGORY, event_label: 'Click - Meta', value: 'Meta' })
    if (FC_CLIENTID && !["dosequis", "tecate", "mixx", "cristal", "heineken00", "solMezclas", 'massive', "miller", "indio", "amstel","cartablanca"].includes(THEME)) {
      window.FB.login((response) => {
        if (response?.authResponse) {
          loginSDK(response)
        } else {
          console.log('User cancelled login or did not fully authorize.');
        }
      }, { scope: 'email,user_birthday,user_likes' });
    } else {
      return goToUrlFacebook(null, utmServices.utmRead())
    }
  }

  return (
    <div onClick={handleClick} className="cursor-pointer">
      <FacebookIcon className='facebook' />
    </div>
  )
}

export default Facebook
